import {RoomBaseApiService} from "@/core/service/room.baseapi.service";
import {ErrorWrapper, ResponseWrapper} from "@/core/service/util";

class RoomCBTService extends RoomBaseApiService {
    /**
     * CBT 목록 조회
     */
    async getCBTList(params) {
        try {
            const response = await this.request().get(`/api/v3/cbts`, { params });
            return new ResponseWrapper(response, response.data);
        } catch (error) {
            throw new ErrorWrapper(error);
        }
    }

    /**
     * CBT 문제지 업로드
     */
    async uploadCBTDoc(params) {
        try {
            const data = new FormData();
            data.append("multipartFile", params.multipartFile);
            data.append("filename", params.filename)
            data.append("roomId", params.roomId)
            data.append("groupId", params.groupId)

            const response = await this.request().put(`/api/v3/cbts/uploadCbtDoc`, data, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return new ResponseWrapper(response, response.data.data);
        } catch (error) {
            throw new ErrorWrapper(error);
        }
    }

    /**
     * CBT 조회
     */
    async getCBT(params) {
        try {
            const response = await this.request().get(`/api/v3/cbts/${params.cbtId}`, {
                params: {
                    roomId: this.getRoomId()
                }
            });
            return new ResponseWrapper(response, response.data.data);
        } catch (error) {
            throw new ErrorWrapper(error);
        }
    }

    /**
     * CBT 생성
     */
    async createCBT(params) {
        try {
            const response = await this.request().post(`/api/v3/cbts`, params);
            return new ResponseWrapper(response, response.data.data);
        } catch (error) {
            throw new ErrorWrapper(error);
        }
    }

    /**
     * CBT 삭제
     */
    async deleteCBT(params) {
        try {
            const response = await this.request().delete(`/api/v3/cbts`, { params });
            return new ResponseWrapper(response, response.data.data);
        } catch (error) {
            throw new ErrorWrapper(error);
        }
    }

    /**
     * 풀이중인 CBT 조회
     */
    async getRoomCBT() {
        try {
            const response = await this.request().get(
              `/api/v3/room/${this.getRoomId()}/roomcbts/info`
            );
            return new ResponseWrapper(response, response.data.data);
        } catch (error) {
            throw new ErrorWrapper(error);
        }
    }

    /**
     * 풀이중인 CBT 사용자 목록 조회
     */
    async getRoomCBTUserList(params) {
        try {
            const response = await this.request().get(
              `/api/v3/room/${this.getRoomId()}/roomcbts/${params.roomCbtId}/users/infoAll`
            );
            return new ResponseWrapper(response, response.data.data);
        } catch (error) {
            throw new ErrorWrapper(error);
        }
    }

    /**
     * 풀이중인 CBT 사용자 존재 여부 조회
     */
    async getRoomCBTExistUser(params) {
        try {
            const response = await this.request().get(`/api/v3/room/${this.getRoomId()}/roomcbts/${params.roomCbtId}/users/${params.userId}/user`);
            return new ResponseWrapper(response, response.data.data);
        } catch (error) {
            throw new ErrorWrapper(error);
        }
    }

    /**
     * 풀이중인 CBT 제출
     */
    async completeRoomCBTUser(params) {
        try {
            const response = await this.request().patch(`/api/v3/room/${this.getRoomId()}/roomcbts/${params.roomCbtId}/users/${params.userId}/user`);
            return new ResponseWrapper(response, response.data.data);
        } catch (error) {
            throw new ErrorWrapper(error);
        }
    }

    /**
     * 풀이중인 CBT 사용자 답안 조회
     */
    async getRoomCBTUserAnswerList(params) {
        try {
            const response = await this.request().get(`/api/v3/room/${this.getRoomId()}/roomcbts/${params.roomCbtId}/users/${params.userId}`);
            return new ResponseWrapper(response, response.data.data);
        } catch (error) {
            throw new ErrorWrapper(error);
        }
    }

    async createRoomCBTUser(params) {
        try {
            const response = await this.request().post(`/api/v3/room/${this.getRoomId()}/roomcbts/${params.roomCbtId}/users`, params);
            return new ResponseWrapper(response, response.data.data);
        } catch (error) {
            throw new ErrorWrapper(error);
        }
    }

    async getRoomCBTDetail(params) {
        try {
            const response = await this.request().get(`/api/v3/room/${this.getRoomId()}/roomcbts/${params.roomCbtId}/detail`);
            return new ResponseWrapper(response, response.data.data);
        } catch (error) {
            throw new ErrorWrapper(error);
        }
    }

    async startRoomCBT(params) {
        try {
            const response = await this.request().post(
                `/api/v3/room/${this.getRoomId()}/roomcbts/${params.cbtId}/begin`,
                {
                    docId: params.docId,
                    duration: params.duration,
                }
            );
            return new ResponseWrapper(response, response.data.data);
        } catch (error) {
            throw new ErrorWrapper(error);
        }
    }

    async endRoomCBT(params) {
        try {
            const response = await this.request().patch(`/api/v3/room/${this.getRoomId()}/roomcbts/${params.roomCbtId}/end`);
            return new ResponseWrapper(response, response.data.data);
        } catch (error) {
            throw new ErrorWrapper(error);
        }
    }

    async submitRoomCBTUserAnswerList(params) {
        try {
            const response = await this.request().post(`/api/v3/room/${this.getRoomId()}/roomcbts/${params.roomCbtId}/users/${params.userId}/answers`, {
                userQuestion: params.userQuestion,
            });
            return new ResponseWrapper(response, response.data.data);
        } catch (error) {
            throw new ErrorWrapper(error);
        }
    }

    /**
     * 풀이중인 CBT Doc 조회
     */
    async getRoomCBTUserDoc(params) {
        try {
            const response = await this.request().get(`/api/v3/room/${this.getRoomId()}/roomcbts/${params.roomCbtId}/users/${params.userId}/doc`);
            return new ResponseWrapper(response, response.data.data);
        } catch (error) {
            throw new ErrorWrapper(error);
        }
    }

    /**
     * 풀이중인 CBT Doc Page 변경
     */
    async changeRoomCBTUserDocPage(params) {
        try {
            const response = await this.request().post(`/api/v3/room/${this.getRoomId()}/roomcbts/${params.roomCbtId}/users/${params.userId}/changeCbtDocPage`, {
                roomGroupId: params.roomGroupId,
                docId: params.docId,
                docPageId: params.docPageId
            });
            return new ResponseWrapper(response, response.data.data);
        } catch (error) {
            throw new ErrorWrapper(error);
        }
    }

    /**
     * CBT 출제 이력 목록 조회
     */
    async getCBTLogList(params) {
        try {
            const response = await this.request().get(`/api/v3/room/${this.getRoomId()}/roomcbts`, { params });
            return new ResponseWrapper(response, response.data);
        } catch (error) {
            throw new ErrorWrapper(error);
        }
    }

    /**
     * CBT 출제 이력 상세 조회
     */
    async getCBTLog(params) {
        try {
            const response = await this.request().get(`/api/v3/room/${this.getRoomId()}/roomcbts/${params.roomCbtId}`);
            return new ResponseWrapper(response, response.data);
        } catch (error) {
            throw new ErrorWrapper(error);
        }
    }
}

export const roomCBTService = new RoomCBTService();